/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  isLivekitFocus,
  isLivekitFocusConfig,
  type JoinSessionConfig,
  type LivekitFocus,
  type LivekitFocusActive,
  type MatrixRTCSession,
} from "matrix-js-sdk/lib/matrixrtc";
import { logger } from "matrix-js-sdk/lib/logger";
import { AutoDiscovery } from "matrix-js-sdk/lib/autodiscovery";

import { PosthogAnalytics } from "./analytics/PosthogAnalytics";
import { Config } from "./config/Config";
import { ElementWidgetActions, widget, type WidgetHelpers } from "./widget";
import { MatrixRTCFocusMissingError } from "./utils/errors";
import { getUrlParams } from "./UrlParams";
import {
  getSFUConfigWithOpenID,
  SFUCredentialsError,
} from "./livekit/openIDSFU.ts";
import { callTraceJournal, describeTraceError, safeOrigin } from "./telemetry";
import { throwIfAborted } from "./recovery/abort";

const FOCI_WK_KEY = "org.matrix.msc4143.rtc_foci";

export function makeActiveFocus(): LivekitFocusActive {
  return {
    type: "livekit",
    focus_selection: "oldest_membership",
  };
}

/**
 * Строит preferred foci в прежнем порядке приоритетов, не выполняя warm-up и
 * не запуская MatrixRTC membership.
 */
export async function buildPreferredLivekitFoci(
  rtcSession: MatrixRTCSession,
  livekitAlias: string,
): Promise<LivekitFocus[]> {
  logger.log("Start building foci_preferred list: ", rtcSession.room.roomId);

  const preferredFoci: LivekitFocus[] = [];

  // Make the Focus from the running rtc session the highest priority one
  // This minimizes how often we need to switch foci during a call.
  const focusInUse = rtcSession.getFocusInUse();
  if (focusInUse && isLivekitFocus(focusInUse)) {
    logger.log("Adding livekit focus from oldest member: ", focusInUse);
    preferredFoci.push(focusInUse);
  }

  // Prioritize the .well-known/matrix/client, if available, over the configured SFU
  const domain = rtcSession.room.client.getDomain();
  if (domain) {
    // we use AutoDiscovery instead of relying on the MatrixClient having already
    // been fully configured and started
    const wellKnownFoci = (await AutoDiscovery.getRawClientConfig(domain))?.[
      FOCI_WK_KEY
    ];
    if (Array.isArray(wellKnownFoci)) {
      const validWellKnownFoci = wellKnownFoci
        .filter((f) => !!f)
        .filter(isLivekitFocusConfig)
        .map((wellKnownFocus) => {
          logger.log("Adding livekit focus from well known: ", wellKnownFocus);
          return { ...wellKnownFocus, livekit_alias: livekitAlias };
        });
      preferredFoci.push(...validWellKnownFoci);
    }
  }

  const urlFromConf = Config.get().livekit?.livekit_service_url;
  if (urlFromConf) {
    const focusFormConf: LivekitFocus = {
      type: "livekit",
      livekit_service_url: urlFromConf,
      livekit_alias: livekitAlias,
    };
    logger.log("Adding livekit focus from config: ", focusFormConf);
    preferredFoci.push(focusFormConf);
  }

  if (preferredFoci.length === 0)
    throw new MatrixRTCFocusMissingError(domain ?? "");
  return preferredFoci;

  // TODO: we want to do something like this:
  //
  // const focusOtherMembers = await focusFromOtherMembers(
  //   rtcSession,
  //   livekitAlias,
  // );
  // if (focusOtherMembers) preferredFoci.push(focusOtherMembers);
}

/**
 * Выбирает owned focus для необязательного warm-up. Focus действующего
 * oldest membership не прогревается: он уже используется другим участником.
 */
export function selectLivekitFocusForWarmUp(
  rtcSession: MatrixRTCSession,
  preferredFoci: readonly LivekitFocus[],
): LivekitFocus | undefined {
  const focusInUse = rtcSession.getFocusInUse();
  const firstOwnedIndex = focusInUse && isLivekitFocus(focusInUse) ? 1 : 0;
  return preferredFoci[firstOwnedIndex];
}

/** Выполняет одну попытку создания SFU room до публикации membership. */
export async function warmUpLivekitFocus(
  rtcSession: MatrixRTCSession,
  focus: LivekitFocus,
  failurePolicy: "propagate" | "best_effort" = "propagate",
): Promise<void> {
  callTraceJournal.record({
    eventType: "sfu.focus_changed",
    stage: "sfu",
    outcome: "state_changed",
    payload: {
      focusOrigin: safeOrigin(focus.livekit_service_url),
      source: "warm_up",
    },
  });
  try {
    await getSFUConfigWithOpenID(rtcSession.room.client, focus);
  } catch (error) {
    // Legacy user entry считал JWT warm-up best effort. OpenID failure при этом
    // и раньше прерывал Join, поэтому подавляется только typed JWT failure.
    if (
      failurePolicy === "best_effort" &&
      error instanceof SFUCredentialsError &&
      error.operation === "jwt"
    ) {
      logger.warn("Не удалось выполнить необязательный SFU warm-up", error);
      return;
    }
    throw error;
  }
}

export interface TechnicalRTCSessionJoinOptions {
  readonly preferredFoci: readonly LivekitFocus[];
  readonly joinConfig: JoinSessionConfig;
}

/**
 * Запускает только техническую MatrixRTC membership. Операция намеренно не
 * выполняет retry, leave, analytics, ringing или widget-команды.
 */
export function technicalJoinRTCSession(
  rtcSession: MatrixRTCSession,
  options: TechnicalRTCSessionJoinOptions,
): void {
  rtcSession.joinRoomSession(
    [...options.preferredFoci],
    makeActiveFocus(),
    options.joinConfig,
  );
}

export interface BuildTechnicalRTCSessionJoinOptions {
  readonly encryptMedia: boolean;
  readonly useNewMembershipManager?: boolean;
  readonly useExperimentalToDeviceTransport?: boolean;
  readonly notificationType?: JoinSessionConfig["notificationType"];
  readonly callIntent?: JoinSessionConfig["callIntent"];
}

/** Собирает SDK options отдельно от запуска membership. */
export function buildTechnicalRTCSessionJoinConfig({
  encryptMedia,
  useNewMembershipManager = true,
  useExperimentalToDeviceTransport = false,
  notificationType,
  callIntent,
}: BuildTechnicalRTCSessionJoinOptions): JoinSessionConfig {
  const { features, matrix_rtc_session: matrixRtcSessionConfig } = Config.get();
  const useDeviceSessionMemberEvents =
    features?.feature_use_device_session_member_events;

  return {
    notificationType,
    callIntent,
    useNewMembershipManager,
    manageMediaKeys: encryptMedia,
    ...(useDeviceSessionMemberEvents !== undefined && {
      useLegacyMemberEvents: !useDeviceSessionMemberEvents,
    }),
    delayedLeaveEventRestartMs:
      matrixRtcSessionConfig?.delayed_leave_event_restart_ms,
    delayedLeaveEventDelayMs:
      matrixRtcSessionConfig?.delayed_leave_event_delay_ms,
    delayedLeaveEventRestartLocalTimeoutMs:
      matrixRtcSessionConfig?.delayed_leave_event_restart_local_timeout_ms,
    networkErrorRetryMs: matrixRtcSessionConfig?.network_error_retry_ms,
    makeKeyDelay: matrixRtcSessionConfig?.wait_for_key_rotation_ms,
    membershipEventExpiryMs: matrixRtcSessionConfig?.membership_event_expiry_ms,
    useExperimentalToDeviceTransport,
  };
}

/**
 * Выполняет technical rejoin без analytics/widget side effects.
 * Warm-up и одноразовый SDK notification включаются только явно владельцем
 * пользовательской call session.
 */
export async function technicalRejoinRTCSession(
  rtcSession: MatrixRTCSession,
  options: BuildTechnicalRTCSessionJoinOptions,
  warmUp = false,
  signal?: AbortSignal,
): Promise<void> {
  if (signal) throwIfAborted(signal);
  let preferredFoci: LivekitFocus[];
  try {
    preferredFoci = await buildPreferredLivekitFoci(
      rtcSession,
      rtcSession.room.roomId,
    );
  } catch (error) {
    if (signal?.aborted) throwIfAborted(signal);
    throw error;
  }
  if (signal) throwIfAborted(signal);
  const focusToWarmUp = selectLivekitFocusForWarmUp(rtcSession, preferredFoci);
  if (warmUp && focusToWarmUp) {
    try {
      await warmUpLivekitFocus(rtcSession, focusToWarmUp);
    } catch (error) {
      if (signal?.aborted) throwIfAborted(signal);
      throw error;
    }
    if (signal) throwIfAborted(signal);
  }
  if (signal) throwIfAborted(signal);
  technicalJoinRTCSession(rtcSession, {
    preferredFoci,
    joinConfig: buildTechnicalRTCSessionJoinConfig(options),
  });
}

/** Фиксирует одноразовые side effects пользовательского входа в звонок. */
export function recordUserRTCSessionEntry(rtcSession: MatrixRTCSession): void {
  PosthogAnalytics.instance.eventCallEnded.cacheStartCall(new Date());
  PosthogAnalytics.instance.eventCallStarted.track(rtcSession.room.roomId);
}

/** Отправляет host-приложению одноразовый факт пользовательского Join. */
export async function sendWidgetJoinCall(): Promise<void> {
  if (!widget) return;
  try {
    await widget.api.transport.send(ElementWidgetActions.JoinCall, {});
  } catch (error) {
    logger.error("Failed to send join action", error);
  }
}

/**
 * Выполняет стандартную техническую попытку входа в MatrixRTC.
 *
 * Операция не владеет пользовательскими analytics/widget side effects и не
 * выполняет hard recovery: вызывающий код сам решает, куда передать ошибку.
 */
export async function attemptRTCSessionEntry(
  rtcSession: MatrixRTCSession,
  encryptMedia: boolean,
  useNewMembershipManager = true,
  useExperimentalToDeviceTransport = false,
): Promise<void> {
  const startedAt = performance.now();
  const relatedEventId = callTraceJournal.record({
    eventType: "matrix_rtc.join_started",
    stage: "matrix_rtc",
    outcome: "started",
  });
  callTraceJournal.setAttemptState("joining");

  // right now we assume everything is a room-scoped call
  const livekitAlias = rtcSession.room.roomId;
  const { sendNotificationType: notificationType, callIntent } = getUrlParams();
  try {
    const preferredFoci = await buildPreferredLivekitFoci(
      rtcSession,
      livekitAlias,
    );
    const focusToWarmUp = selectLivekitFocusForWarmUp(
      rtcSession,
      preferredFoci,
    );
    if (focusToWarmUp) {
      await warmUpLivekitFocus(rtcSession, focusToWarmUp, "best_effort");
    }
    technicalJoinRTCSession(rtcSession, {
      preferredFoci,
      joinConfig: buildTechnicalRTCSessionJoinConfig({
        encryptMedia,
        useNewMembershipManager,
        useExperimentalToDeviceTransport,
        notificationType,
        callIntent,
      }),
    });
  } catch (error) {
    callTraceJournal.record({
      eventType: "matrix_rtc.join_state_changed",
      stage: "matrix_rtc",
      severity: "error",
      outcome: "failed",
      reasonCode: "join_room_session_failed",
      relatedEventId,
      durationMs: Math.round(performance.now() - startedAt),
      payload: {
        operation: "join_room_session",
        ...describeTraceError(error),
      },
    });
    throw error;
  }
}

export async function enterRTCSession(
  rtcSession: MatrixRTCSession,
  encryptMedia: boolean,
  useNewMembershipManager = true,
  useExperimentalToDeviceTransport = false,
): Promise<void> {
  recordUserRTCSessionEntry(rtcSession);

  // This must be called before we start trying to join the call, as we need to
  // have started tracking by the time calls start getting created.
  // groupCallOTelMembership?.onJoinCall();

  await attemptRTCSessionEntry(
    rtcSession,
    encryptMedia,
    useNewMembershipManager,
    useExperimentalToDeviceTransport,
  );
  await sendWidgetJoinCall();
}

const widgetPostHangupProcedure = async (
  widget: WidgetHelpers,
  cause: "user" | "error",
  promiseBeforeHangup?: Promise<unknown>,
): Promise<void> => {
  try {
    await widget.api.setAlwaysOnScreen(false);
  } catch (e) {
    logger.error("Failed to set call widget `alwaysOnScreen` to false", e);
  }

  // Wait for any last bits before hanging up.
  await promiseBeforeHangup;
  // We send the hangup event after the memberships have been updated
  // calling leaveRTCSession.
  // We need to wait because this makes the client hosting this widget killing the IFrame.
  try {
    await widget.api.transport.send(ElementWidgetActions.HangupCall, {});
  } catch (e) {
    logger.error("Failed to send hangup action", e);
  }
  // On a normal user hangup we can shut down and close the widget. But if an
  // error occurs we should keep the widget open until the user reads it.
  if (cause === "user" && !getUrlParams().returnToLobby) {
    try {
      await widget.api.transport.send(ElementWidgetActions.Close, {});
    } catch (e) {
      logger.error("Failed to send close action", e);
    }
    widget.api.transport.stop();
  }
};

export async function leaveRTCSession(
  rtcSession: MatrixRTCSession,
  cause: "user" | "error",
  promiseBeforeHangup?: Promise<unknown>,
): Promise<void> {
  const startedAt = performance.now();
  const relatedEventId = callTraceJournal.record({
    eventType: "matrix_rtc.leave_started",
    stage: "matrix_rtc",
    outcome: "started",
    payload: { cause },
  });
  try {
    await rtcSession.leaveRoomSession();
    if (widget) {
      await widgetPostHangupProcedure(widget, cause, promiseBeforeHangup);
    } else {
      await promiseBeforeHangup;
    }
    callTraceJournal.record({
      eventType: "matrix_rtc.leave_succeeded",
      stage: "matrix_rtc",
      outcome: "succeeded",
      relatedEventId,
      durationMs: Math.round(performance.now() - startedAt),
    });
    callTraceJournal.finishAttempt(
      cause === "user" ? "ended" : "failed",
      cause === "user" ? "user_left" : "fatal_failure",
      "matrix_rtc",
      cause === "user" ? "user_leave" : "error_leave",
    );
  } catch (error) {
    callTraceJournal.record({
      eventType: "matrix_rtc.leave_failed",
      stage: "matrix_rtc",
      severity: "error",
      outcome: "failed",
      relatedEventId,
      durationMs: Math.round(performance.now() - startedAt),
      payload: describeTraceError(error),
    });
    throw error;
  }
}
